import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
// Static Fallbacks - these are essential
import enCommonTranslations from '../locales/en/common.json';
import frCommonTranslations from '../locales/fr/common.json';
import enMentorTranslations from '../locales/en/mentor.json';
import frMentorTranslations from '../locales/fr/mentor.json';
import { translations as metaTranslations } from './translations'; // Local meta

// English worker static fallbacks
import enAndrea from '../locales/en/workers/andrea.json';
import enAria from '../locales/en/workers/aria.json';
import enMicah from '../locales/en/workers/micah.json';
import enDan from '../locales/en/workers/dan.json';
import enEdward from '../locales/en/workers/edward.json';
import enElen from '../locales/en/workers/elen.json';
import enJade from '../locales/en/workers/jade.json';
import enJanie from '../locales/en/workers/janie.json';
import enSolvej from '../locales/en/workers/solvej.json';
import enGen from '../locales/en/workers/gen.json';
import enAtlas from '../locales/en/workers/atlas.json';

// French worker static fallbacks
import frAria from '../locales/fr/workers/aria.json';
import frSolvej from '../locales/fr/workers/solvej.json';
import frAtlas from '../locales/fr/workers/atlas.json';
import frGen from '../locales/fr/workers/gen.json';
import frJanie from '../locales/fr/workers/janie.json';
import frJade from '../locales/fr/workers/jade.json';
import frElen from '../locales/fr/workers/elen.json';
import frEdward from '../locales/fr/workers/edward.json';
import frDan from '../locales/fr/workers/dan.json';
import frMicah from '../locales/fr/workers/micah.json';
// frAndrea will fallback to enAndrea as per original structure

// Pre-build static translations once at module load
const STATIC_TRANSLATIONS_CACHE = {
  en: (() => {
    const baseCommon = enCommonTranslations;
    const mentor = enMentorTranslations;
    const localMeta = metaTranslations.en;

    const workerFallbacks = {
      andrea: enAndrea,
      aria: enAria,
      micah: enMicah,
      dan: enDan,
      edward: enEdward,
      elen: enElen,
      jade: enJade,
      janie: enJanie,
      solvej: enSolvej,
      gen: enGen,
      atlas: enAtlas,
    };

    const digitalWorkersData = { 
      ...(baseCommon.digitalWorkers || {}), 
      workers: {
        ...((baseCommon.digitalWorkers && baseCommon.digitalWorkers.workers) || {}),
        ...workerFallbacks
      }
    };

    return {
      ...baseCommon,
      ...localMeta,
      mentor,
      digitalWorkers: digitalWorkersData,
    };
  })(),
  
  fr: (() => {
    const baseCommon = frCommonTranslations;
    const mentor = frMentorTranslations;
    const localMeta = metaTranslations.fr || metaTranslations.en;

    const workerFallbacks = {
      andrea: enAndrea, // frAndrea falls back to en
      aria: frAria,
      micah: frMicah,
      dan: frDan,
      edward: frEdward,
      elen: frElen,
      jade: frJade,
      janie: frJanie,
      solvej: frSolvej,
      gen: frGen,
      atlas: frAtlas,
    };

    const digitalWorkersData = { 
      ...(baseCommon.digitalWorkers || {}), 
      workers: {
        ...((baseCommon.digitalWorkers && baseCommon.digitalWorkers.workers) || {}),
        ...workerFallbacks
      }
    };

    return {
      ...baseCommon,
      ...localMeta,
      mentor,
      digitalWorkers: digitalWorkersData,
    };
  })()
};

// Fast static translations getter
const getStaticFallbackTranslations = (language) => {
  return STATIC_TRANSLATIONS_CACHE[language] || STATIC_TRANSLATIONS_CACHE.en;
};

// Legacy function for backward compatibility - now just a wrapper
const buildStaticFallbackTranslations = getStaticFallbackTranslations;

// Manifest of dynamic translation files and their target paths in the translation object
const DYNAMIC_TRANSLATION_FILES_MANIFEST = {
  // Key: path in `activeTranslations` object (use '.' for nesting)
  // Value: file path on CDN relative to /text/{version}/{lang}/
  'common': 'common.json', // Special case: keys merged at root
  'mentor': 'mentor.json', // Merged into activeTranslations.mentor

  // Worker files
  'digitalWorkers.workers.andrea': 'workers/andrea.json',
  'digitalWorkers.workers.aria': 'workers/aria.json',
  'digitalWorkers.workers.micah': 'workers/micah.json',
  'digitalWorkers.workers.dan': 'workers/dan.json',
  'digitalWorkers.workers.edward': 'workers/edward.json',
  'digitalWorkers.workers.elen': 'workers/elen.json',
  'digitalWorkers.workers.jade': 'workers/jade.json',
  'digitalWorkers.workers.janie': 'workers/janie.json',
  'digitalWorkers.workers.solvej': 'workers/solvej.json',
  'digitalWorkers.workers.gen': 'workers/gen.json',
  'digitalWorkers.workers.atlas': 'workers/atlas.json',
  // Add other files/namespaces like 'config/translations_js_export.json' here
  // 'config_translations_js_export': 'config/translations_js_export.json', 
};

// Constants
const STORAGE_KEY_LANGUAGE = 'language';
const DEFAULT_LANGUAGE = 'en';
const CDN_BASE_URL = 'https://cdn.nexastaff.ai/text';
const CURRENT_VERSION_API = '/api/cms/current-version';
const PREVIEW_VERSION_PARAM = 'cms_preview_version';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Helper to set a nested value in an object based on a path string
const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
      current[keys[i]] = {};
    }
    current = current[keys[i]];
  }
  // Merge the new value with existing, if current target is an object
  if (typeof current[keys[keys.length - 1]] === 'object' && typeof value === 'object' && !Array.isArray(value)) {
     current[keys[keys.length - 1]] = { ...current[keys[keys.length - 1]], ...value };
  } else {
    current[keys[keys.length - 1]] = value;
  }
};


// Helper function to get nested value (remains the same)
const getNestedValue = (obj, pathKeys) => { // pathKeys is already an array
  return pathKeys.reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

const getPreviewVersionFromUrl = () => {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    return params.get(PREVIEW_VERSION_PARAM);
  }
  return null;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(STORAGE_KEY_LANGUAGE) || DEFAULT_LANGUAGE;
    }
    return DEFAULT_LANGUAGE;
  });

  const [liveVersion, setLiveVersion] = useState(null);
  
  // Always start with empty translations - static fallbacks are only for error recovery
  const [activeTranslations, setActiveTranslations] = useState(() => {
    const previewVersion = getPreviewVersionFromUrl();
    if (previewVersion) {
      console.log(`[LanguageContext Init] Preview mode detected (${previewVersion}). Starting with empty translations.`);
    } else {
      console.log(`[LanguageContext Init] Normal mode. Starting with empty translations - static fallbacks only for errors.`);
    }
    return {};
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isContentReady, setIsContentReady] = useState(false);

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY_LANGUAGE, lang);
    }
    
    // Reset loading states
    setActiveTranslations({});
    setIsLoading(true);
    setIsContentReady(false);
  }, []);

  useEffect(() => {
    console.log(`[LC Mount Effect] RUNNING. iframe href: ${typeof window !== "undefined" ? window.location.href : "SSR"}`);
    
    const initialPreviewVersion = getPreviewVersionFromUrl();

    if (initialPreviewVersion) {
      console.log(`[LC Mount Effect] Preview mode detected: ${initialPreviewVersion}. Skipping live version API call and going directly to preview content.`);
      setLiveVersion(initialPreviewVersion);
      // activeTranslations is already empty from initialization, isLoading is already true
      return;
    }

    console.log("[LC Mount Effect] Normal mode - No preview version in URL. Fetching live version from API.");
    setIsLoading(true);
    const fetchLiveVersionFromApi = async () => {
      try {
        const response = await fetch(CURRENT_VERSION_API);
        if (!response.ok) {
          console.warn(`[LC Mount Effect] API for current-version failed: ${response.status}. Using static fallbacks.`);
          setLiveVersion(null);
          setIsLoading(false);
          return;
        }
        const versionInfo = await response.json();
        if (versionInfo && versionInfo.live) {
          console.log(`[LC Mount Effect] API returned live version: ${versionInfo.live}. Setting state.`);
          setLiveVersion(versionInfo.live);
        } else {
          console.error("[LC Mount Effect] Live version not found in API response", versionInfo);
          setLiveVersion(null);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("[LC Mount Effect] Error fetching live version from API:", error);
        setLiveVersion(null);
        setIsLoading(false);
      }
    };
    fetchLiveVersionFromApi();
  }, []);

  useEffect(() => {
    if (!liveVersion || !language) {
      console.log(`[LC DynamicFetch Effect] Skipping fetch: liveVersion ('${liveVersion}') or language ('${language}') is missing.`);
      return;
    }

    const fetchAllDynamicTranslations = async () => {
      setIsLoading(true); 
      
      const isPreviewMode = liveVersion === getPreviewVersionFromUrl();
      console.log(`[LC DynamicFetch] ${isPreviewMode ? 'Preview' : 'Normal'} mode: Starting with empty translations.`);

      const cdnBaseUrl = `${CDN_BASE_URL}/${liveVersion}/${language}`;
      
      if (isPreviewMode) {
        console.log("[LC DynamicFetch] Preview mode: Cache-busting JSON files.");
      } else {
        console.log(`[LC DynamicFetch] Normal mode: No JSON cache-busting.`);
      }

      console.log(`[LC DynamicFetch] Effective CDN Base URL for fetches: ${cdnBaseUrl}`);

      const fetchPromises = [];

      for (const targetPath in DYNAMIC_TRANSLATION_FILES_MANIFEST) {
        const filePath = DYNAMIC_TRANSLATION_FILES_MANIFEST[targetPath];
        let fetchUrl = `${cdnBaseUrl}/${filePath}`;

        if (isPreviewMode) {
            fetchUrl += `?preview_ts=${Date.now()}`;
        }

        fetchPromises.push(
          fetch(fetchUrl)
            .then(response => {
              if (response.ok) {
                return response.json().then(jsonData => ({ targetPath, jsonData, success: true }));
              } else {
                console.warn(`Failed to fetch ${fetchUrl}: ${response.status}`);
                return { targetPath, success: false, status: response.status };
              }
            })
            .catch(error => {
              console.error(`Error fetching ${fetchUrl}:`, error);
              return { targetPath, success: false, error };
            })
        );
      }

      try {
        const results = await Promise.all(fetchPromises);

        // Start with empty base
        let baseForMerge = {};

        // Merge successful fetches
        const successfulResults = results.filter(r => r.success);
        successfulResults.forEach(result => {
          if (result.targetPath === "common") {
            baseForMerge = { ...baseForMerge, ...result.jsonData };
          } else {
            setNestedValue(baseForMerge, result.targetPath, result.jsonData);
          }
        });
        
        // If no files loaded successfully, fallback to static (error recovery)
        if (successfulResults.length === 0) {
          console.warn("[LC DynamicFetch] No files loaded successfully. Using static fallbacks as error recovery.");
          baseForMerge = getStaticFallbackTranslations(language);
        } else {
          console.log(`[LC DynamicFetch] Successfully loaded ${successfulResults.length}/${results.length} translation files.`);
        }
        
        setActiveTranslations(baseForMerge);
        
        // Mark content as ready after successful loading
        setTimeout(() => {
          setIsContentReady(true);
        }, 100); // Small delay to ensure DOM updates
        
      } catch (error) {
        console.error("[LanguageContext] Error processing dynamic translation fetches:", error);
        console.warn("[LanguageContext] Using static fallbacks as error recovery.");
        setActiveTranslations(getStaticFallbackTranslations(language));
        
        // Even with fallbacks, mark as ready
        setTimeout(() => {
          setIsContentReady(true);
        }, 100);
      } finally {
        setIsLoading(false);
        console.log("[LanguageContext] Finished fetching dynamic translations. Loading complete.");
      }
    };

    fetchAllDynamicTranslations();
  }, [liveVersion, language]);

  useEffect(() => {
    if (!isLoading) {
      console.log("[LanguageContext] Final activeTranslations:", activeTranslations);
      if (language === 'en') {
        console.log("[LanguageContext] EN Footer data:", activeTranslations?.footer);
        console.log("[LanguageContext] EN footer.shareWithColleagues:", activeTranslations?.footer?.shareWithColleagues);
      } else if (language === 'fr') {
        console.log("[LanguageContext] FR Footer data:", activeTranslations?.footer);
        console.log("[LanguageContext] FR footer.shareWithColleagues:", activeTranslations?.footer?.shareWithColleagues);
      }
    }
  }, [isLoading, activeTranslations, language]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = getNestedValue(activeTranslations, keys);

    const isActiveTranslationsEmpty = Object.keys(activeTranslations).length === 0;
    
    // During loading state (both preview and normal), show keys instead of static fallbacks
    // This prevents content flash in all modes
    if (value === undefined && isActiveTranslationsEmpty && isLoading) {
      return key; // Return key instead of falling back to static during loading
    }
    
    // Only use static fallbacks after loading completes and we still don't have the value
    // This is for error recovery when live content fails to load
    if (value === undefined && !isLoading) {
      const currentLangStaticFallbacks = getStaticFallbackTranslations(language);
      value = getNestedValue(currentLangStaticFallbacks, keys);
    }

    if (value === undefined && language !== 'en' && !isLoading) {
      const englishFallbackData = getStaticFallbackTranslations('en');
      value = getNestedValue(englishFallbackData, keys);
    }
    
    if (value === undefined) {
      return key; 
    }
    return value;
  }, [activeTranslations, language, isLoading]);

  // Safe translation helper that returns appropriate defaults for arrays/objects during loading
  const tSafe = useCallback((key, expectedType = 'string') => {
    const result = t(key);
    
    // If we're loading OR content isn't ready yet, return safe defaults
    const isLoadingState = isLoading || !isContentReady;
    
    if (isLoadingState && result === key) {
      switch (expectedType) {
        case 'array':
          return [];
        case 'object':
          return {};
        default:
          return key;
      }
    }
    
    // Additional safety: if result is a string but we expected array/object
    if (typeof result === 'string' && result === key) {
      switch (expectedType) {
        case 'array':
          return [];
        case 'object':
          return {};
        default:
          return result;
      }
    }
    
    return result;
  }, [t, isLoading, isContentReady]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t, 
      tSafe, 
      isLoading, 
      isContentReady,
      liveVersion, 
      staticFallbackOnError: !liveVersion && !isLoading 
    }}>
      {children}
    </LanguageContext.Provider>
  );
}; 